import { computed, observable, makeObservable, actionBound } from "mobx";

export interface PagedParams<K> {
    pageNo: number;

    pageSize: number;

    otherParams?: K;
}

export interface PagedResult<T> {
    total: number;

    list: T[];
}

interface Pagination {
    current: number;

    pageSize: number;

    total: number;

    totalPage: number;

    onChange: (current: number, pageSize: number) => void;

    changeCurrent: (current: number) => void;

    changePageSize: (pageSize: number) => void;
}

export default abstract class PagedStore<T, K = any> {
    constructor() {
        makeObservable(this, {
            loading: observable,
            pageNo: observable,
            pageSize: observable,
            total: observable,
            dataSource: observable,
            otherParams: observable,
            setPageNo: actionBound,
            onChange: actionBound,
            changeCurrent: actionBound,
            changePageSize: actionBound,
            refresh: actionBound,
            pagination: computed,
            loadData: actionBound,
        });
    }

    loading = false;

    pageNo = 1;

    pageSize = 50;

    total = 0;

    dataSource: T[] = [];

    otherParams: K = undefined;

    setPageNo(pageNo: number) {
        this.pageNo = pageNo;
    }

    onChange(current: number, pageSize: number) {
        this.pageNo = current;
        this.pageSize = pageSize;

        this.loadData(this.otherParams);
    }

    changeCurrent(current: number) {
        this.pageNo = current;

        this.loadData(this.otherParams);
    }

    changePageSize(pageSize: number) {
        this.pageSize = pageSize;

        this.loadData(this.otherParams);
    }

    refresh() {
        this.loadData(this.otherParams);
    }

    get pagination(): Pagination {
        return {
            current: this.pageNo,
            pageSize: this.pageSize,
            total: this.total,
            totalPage: Math.ceil(this.total / this.pageSize),
            onChange: this.onChange,
            changeCurrent: this.changeCurrent,
            changePageSize: this.changePageSize,
        };
    }

    async loadData(otherParams?: K) {
        if (this.loading) {
            return;
        }

        this.loading = true;

        this.otherParams = otherParams;

        try {
            const result = await this._loadDataImpl({
                pageNo: this.pageNo,
                pageSize: this.pageSize,
                otherParams,
            });

            this.dataSource = result.list;
            this.total = result.total;

            return result;
        } catch (error) {
            console.error("error:", error);
        } finally {
            this.loading = false;
        }
    }

    abstract _loadDataImpl(params: PagedParams<K>): Promise<PagedResult<T>>;
}
